import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreVertical } from 'react-feather';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonProps } from '../UI/Button';
import { UIProps } from '../../theme/mixins';
import UI from '../UI';

interface OptionProps extends ButtonProps {
  onClick: () => void;
  icon?: ReactNode;
  label: string;
  color?: string;
  active?: boolean;
}

export const Option = ({ icon, label, color, active, onClick, ...props }: OptionProps) => (
  <DropdownMenu.Item asChild>
    <UI.Button
      onClick={onClick}
      sc={{ blank: true, color, strong: active }}
      {...props}
    >
      {icon && (
        <UI.Icon>
          {icon}
        </UI.Icon>
      )}
      {label}
    </UI.Button>
  </DropdownMenu.Item>
);

interface LinkOptionProps extends ButtonProps {
  to: string;
  icon?: ReactNode;
  label: string;
  color?: string;
}

export const LinkOption = ({ icon, label, color, to, ...props }: LinkOptionProps) => (
  <DropdownMenu.Item asChild>
    <UI.LinkButton
      to={to}
      sc={{ blank: true, color }}
      role="link"
      {...props}
    >
      {icon && (
        <UI.Icon>
          {icon}
        </UI.Icon>
      )}
      {label}
    </UI.LinkButton>
  </DropdownMenu.Item>
);

interface ExternalOptionProps extends ButtonProps {
  href: string;
  icon?: ReactNode;
  label: string;
  color?: string;
}

export const ExternalOption = ({ icon, label, color, href, ...props }: ExternalOptionProps) => (
  <DropdownMenu.Item asChild>
    <UI.AButton
      href={href}
      sc={{ blank: true, color }}
      target="_blank"
      rel="noopener norferrer"
      role="link"
      {...props}
    >
      {icon && (
        <UI.Icon>
          {icon}
        </UI.Icon>
      )}
      {label}
    </UI.AButton>
  </DropdownMenu.Item>
);

interface OptionsDropdownProps {
  children: ReactNode;
  trigger?: ReactNode;
  onToggle?: () => void;
  name?: string;
  ariaLabel?: string;
  sc?: UIProps;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

const OptionsDropdown = ({
  children, trigger, onToggle, name, ariaLabel, sc, align = 'end', side = 'bottom',
}: OptionsDropdownProps) => {
  const { t } = useTranslation();

  const [isShowingOptions, setIsShowingOptions] = useState(false);

  const toggleDropdown = (showing: boolean) => {
    if (showing) {
      onToggle?.();
    }

    setIsShowingOptions(showing);
  };

  return (
    <UI.DropdownOptionsContainer sc={sc}>
      <DropdownMenu.Root open={isShowingOptions} onOpenChange={toggleDropdown}>
        <DropdownMenu.Trigger asChild>
          {trigger || (
            <UI.DropdownToggle
              sc={{ blank: true, size: 'sm' }}
              aria-label={ariaLabel || (
                name ? t('more_options_for', { name }) : t('more_options')
              )}
            >
              <UI.Icon>
                <MoreVertical />
              </UI.Icon>
              <UI.Caret />
            </UI.DropdownToggle>
          )}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content style={{ zIndex: 1000 }} align={align} side={side} sideOffset={0}>
            <UI.Dropdown sc={{ position: 'relative', open: true }}>
              {children}
            </UI.Dropdown>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </UI.DropdownOptionsContainer>
  );
};

export interface ContainerWithOptionsProps {
  name?: string;
  ariaLabel?: string;
  renderOptions?: () => ReactNode;
  children: ReactNode;
  sc?: UIProps;
  alignVertical?: 'start' | 'center' | 'end';
}

export const ContainerWithOptions = ({
  name, ariaLabel, children, renderOptions, sc, alignVertical = 'start',
}: ContainerWithOptionsProps) => (
  <UI.GridContainer
    sc={{ columns: renderOptions ? '1fr 35px' : '1fr', gutter: 0.5, alignVertical: 'center', ...sc }}
  >
    <UI.Div>
      {children}
    </UI.Div>
    {renderOptions && (
      <UI.Div style={{ alignSelf: alignVertical, margin: '-4.5px 0' }}>
        <OptionsDropdown name={name} ariaLabel={ariaLabel}>
          {renderOptions()}
        </OptionsDropdown>
      </UI.Div>
    )}
  </UI.GridContainer>
);

export default OptionsDropdown;
